import { AccountApi, type FavoriteKind } from 'api/AccountApi'
import type { OrganizationRef } from 'api/IamApi'
import { WhoAmI } from 'api/schema'
import { createContext, useContext, useEffect, useRef } from 'react'
import { Updater, useImmer } from 'use-immer'
import { LOGIN_ERROR_TYPES, PROGRESS_TYPES } from 'utils/constants'
import {
  FAVORITES_BROADCAST_CHANNEL,
  FAVORITES_STORAGE_KEY,
  clearFavoritePinsStorage,
  parseFavoritePins,
  publishFavoritePins,
  readFavoritesFromStorage,
  sameFavoritePins,
  writeFavoritesToStorage,
  type FavoritePinsSnapshot,
} from 'utils/favoritesSync'

export type UserAudience =
  | 'developer'
  | 'serviceman'
    | 'internal_serviceman'
  | 'facility_manager'
  | 'reseller'
  | 'fleet_ops'

interface IAccountState {
  loginProgress: PROGRESS_TYPES
  restorePasswordProgress: PROGRESS_TYPES
  loginProgressError: LOGIN_ERROR_TYPES
  userLoggedIn: boolean
  id: number
  email?: string
  firstName?: string
  lastName?: string
  audience: UserAudience | null
  organization: OrganizationRef | null
  canManageUsers: boolean
  canUseIam: boolean
  isSuperuser: boolean
  isStaff: boolean
  isActive: boolean
  /** Django auth group ids (see acc WhoAmI `group_ids`). */
  groupIds: number[]
  avatarUrl: string | null
  favoriteRobotIds: number[]
  favoriteFacilityIds: number[]
  favoriteStationIds: number[]
  favoriteOrganizationIds: number[]
}

interface IAccountActions {
  signin: (login: string, password: string) => Promise<any>
  signout: () => Promise<any>
  fetchUser: () => Promise<any>
  restorePassword: (email: string) => Promise<any>
  toggleFavorite: (kind: FavoriteKind, id: number) => Promise<void>
}

export const initialAccountState: IAccountState = {
  loginProgress: PROGRESS_TYPES.IDLE,
  restorePasswordProgress: PROGRESS_TYPES.IDLE,
  loginProgressError: LOGIN_ERROR_TYPES.NONE,
  userLoggedIn: false,
  id: 0,
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  audience: null,
  organization: null,
  canManageUsers: false,
  canUseIam: false,
  isSuperuser: false,
  isStaff: false,
  isActive: false,
  groupIds: [],
  avatarUrl: null,
  favoriteRobotIds: [],
  favoriteFacilityIds: [],
  favoriteStationIds: [],
  favoriteOrganizationIds: [],
}

const hasManagerLikeOrUnrestrictedAccess = (state: Pick<IAccountState, 'audience'>): boolean =>
  state.audience === 'developer' ||
  state.audience === 'reseller' ||
  state.audience === 'facility_manager' ||
  state.audience === 'fleet_ops'

/** Full data scope (all facilities) — developer and fleet_ops audiences. */
export const hasUnrestrictedDataAccess = (state: Pick<IAccountState, 'audience'>): boolean =>
  state.audience === 'developer' || state.audience === 'fleet_ops'

export const canSeeRobotTasksTab = (state: Pick<IAccountState, 'audience'>): boolean =>
  hasManagerLikeOrUnrestrictedAccess(state)

export const canShowRobotRebootControl = (state: Pick<IAccountState, 'audience'>): boolean =>
  hasManagerLikeOrUnrestrictedAccess(state)

export const canEditOrDeleteFacility = (state: Pick<IAccountState, 'audience'>): boolean =>
  hasManagerLikeOrUnrestrictedAccess(state)

export const canDeleteRobotMap = (state: Pick<IAccountState, 'audience'>): boolean =>
  hasManagerLikeOrUnrestrictedAccess(state)

export const UserProviderContext = createContext<{
  state: IAccountState
  setState: Updater<IAccountState>
  actions: IAccountActions
} | null>(null)

UserProviderContext.displayName = 'User'

export const useUser = () => useContext(UserProviderContext)

const idList = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return []
  return raw.map(Number).filter(value => Number.isInteger(value) && value > 0)
}

export const UserProvider = ({ initialState, children }: { initialState: IAccountState; children: JSX.Element }) => {
  const [state, setState] = useImmer(initialState)
  const favoriteRef = useRef({
    robot: initialState.favoriteRobotIds,
    facility: initialState.favoriteFacilityIds,
    station: initialState.favoriteStationIds,
    organization: initialState.favoriteOrganizationIds,
  })
  const favoriteChainRef = useRef(Promise.resolve())
  const userIdRef = useRef(state.id)
  const pinsRevisionRef = useRef(0)
  const pinsPendingRef = useRef(0)
  const seenUserIdRef = useRef<number | null>(null)
  const applyFavoriteIdsRef = useRef<
    (robotIds: number[], facilityIds: number[], stationIds: number[], organizationIds: number[]) => void
  >(() => {})
  userIdRef.current = state.id
  if (seenUserIdRef.current !== state.id) {
    seenUserIdRef.current = state.id
    const storedPins = state.userLoggedIn ? readFavoritesFromStorage() : null
    const currentPins = favoriteRef.current
    pinsRevisionRef.current =
      storedPins && storedPins.userId === state.id && sameFavoritePins(storedPins, currentPins)
        ? storedPins.updatedAt
        : Date.now()
  }

  useEffect(() => {
    favoriteRef.current = {
      robot: state.favoriteRobotIds,
      facility: state.favoriteFacilityIds,
      station: state.favoriteStationIds,
      organization: state.favoriteOrganizationIds,
    }
  }, [state.favoriteRobotIds, state.favoriteFacilityIds, state.favoriteStationIds, state.favoriteOrganizationIds])

  const applyFavoriteIds = (
    robotIds: number[],
    facilityIds: number[],
    stationIds: number[],
    organizationIds: number[]
  ) => {
    favoriteRef.current = { robot: robotIds, facility: facilityIds, station: stationIds, organization: organizationIds }
    setState(draft => {
      draft.favoriteRobotIds = robotIds
      draft.favoriteFacilityIds = facilityIds
      draft.favoriteStationIds = stationIds
      draft.favoriteOrganizationIds = organizationIds
    })
  }
  applyFavoriteIdsRef.current = applyFavoriteIds

  useEffect(() => {
    if (!state.userLoggedIn || !state.id) return undefined
    const userId = state.id

    const onSnapshot = (snapshot: FavoritePinsSnapshot) => {
      if (snapshot.userId !== userId) return
      if (snapshot.updatedAt < pinsRevisionRef.current) return
      pinsRevisionRef.current = snapshot.updatedAt
      if (sameFavoritePins(snapshot, favoriteRef.current)) return
      applyFavoriteIdsRef.current(snapshot.robot, snapshot.facility, snapshot.station, snapshot.organization)
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== FAVORITES_STORAGE_KEY || !event.newValue) return
      try {
        const snapshot = parseFavoritePins(JSON.parse(event.newValue))
        if (snapshot) onSnapshot(snapshot)
      } catch {
        /* ignore malformed payloads */
      }
    }

    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(FAVORITES_BROADCAST_CHANNEL)
      channel.onmessage = (event: MessageEvent) => {
        const snapshot = parseFavoritePins(event.data)
        if (snapshot) onSnapshot(snapshot)
      }
    } catch {
      channel = null
    }

    const refreshPins = () => {
      if (pinsPendingRef.current > 0) return
      const stored = readFavoritesFromStorage()
      if (stored && stored.userId === userId) {
        if (stored.updatedAt >= pinsRevisionRef.current && !sameFavoritePins(stored, favoriteRef.current)) {
          onSnapshot(stored)
        }
        return
      }
      const started = pinsRevisionRef.current
      void AccountApi.fetchUser()
        .then(data => {
          if (pinsPendingRef.current > 0 || pinsRevisionRef.current !== started) return
          const robotIds = idList(data.favorite_robot_ids)
          const facilityIds = idList(data.favorite_facility_ids)
          const stationIds = idList(data.favorite_station_ids)
          const organizationIds = idList(data.favorite_organization_ids)
          if (
            !sameFavoritePins(
              { robot: robotIds, facility: facilityIds, station: stationIds, organization: organizationIds },
              favoriteRef.current
            )
          ) {
            applyFavoriteIdsRef.current(robotIds, facilityIds, stationIds, organizationIds)
          }
          const updatedAt = Date.now()
          pinsRevisionRef.current = updatedAt
          writeFavoritesToStorage({
            userId,
            robot: robotIds,
            facility: facilityIds,
            station: stationIds,
            organization: organizationIds,
            updatedAt,
          })
        })
        .catch(() => undefined)
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPins()
    }

    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisible)
      channel?.close()
    }
  }, [state.id, state.userLoggedIn])

  const actions: IAccountActions = {
    signin: async (login: string, password: string) => {
      try {
        setState((draft: IAccountState) => {
          draft.userLoggedIn = false
          draft.loginProgress = PROGRESS_TYPES.WORK
          draft.loginProgressError = LOGIN_ERROR_TYPES.NONE
        })
        const data = await AccountApi.login(login, password)
        if ((data as any).status === 'error') {
          setState({
            ...state,
            userLoggedIn: false,
            loginProgress: PROGRESS_TYPES.ERROR,
            loginProgressError: LOGIN_ERROR_TYPES.INVALID_EMAIL_OR_PASSWORD,
          })
        } else {
          const accountState = normalizeAccount(data)
          setState({ ...accountState, userLoggedIn: true, loginProgress: PROGRESS_TYPES.SUCCESS })
        }
      } catch (error) {
        setState({
          ...state,
          userLoggedIn: false,
          loginProgress: PROGRESS_TYPES.ERROR,
          loginProgressError: LOGIN_ERROR_TYPES.ERROR,
        })
      }
    },
    signout: async () => {
      try {
        await AccountApi.logout()
        clearFavoritePinsStorage()
        setState({ ...initialAccountState, userLoggedIn: false })
      } catch (error) {
        clearFavoritePinsStorage()
        setState({ ...initialAccountState, userLoggedIn: false })
      }
    },
    fetchUser: async () => {
      const accountState = await getInitialAccountState()
      setState({ ...accountState })
    },
    restorePassword: async (email: string) => {
      try {
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.WORK
        })
        await AccountApi.restorePassword(email)
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.SUCCESS
        })
      } catch (error) {
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.ERROR
        })
      }
    },
    toggleFavorite: (kind, id) => {
      const run = favoriteChainRef.current.then(async () => {
        pinsPendingRef.current += 1
        const previous = {
          robot: favoriteRef.current.robot,
          facility: favoriteRef.current.facility,
          station: favoriteRef.current.station,
          organization: favoriteRef.current.organization,
        }
        const current = previous[kind]
        const optimistic = current.includes(id) ? current.filter(value => value !== id) : [...current, id]
        applyFavoriteIds(
          kind === 'robot' ? optimistic : previous.robot,
          kind === 'facility' ? optimistic : previous.facility,
          kind === 'station' ? optimistic : previous.station,
          kind === 'organization' ? optimistic : previous.organization
        )
        try {
          const startedRevision = pinsRevisionRef.current
          const data = await AccountApi.toggleFavorite(kind, id)
          let confirmed: {
            favorite_robot_ids?: unknown
            favorite_facility_ids?: unknown
            favorite_station_ids?: unknown
            favorite_organization_ids?: unknown
          } = data
          if (pinsRevisionRef.current !== startedRevision) {
            try {
              confirmed = await AccountApi.fetchUser()
            } catch {
              confirmed = data
            }
          }
          const robotIds = idList(confirmed.favorite_robot_ids)
          const facilityIds = idList(confirmed.favorite_facility_ids)
          const stationIds = idList(confirmed.favorite_station_ids)
          const organizationIds = idList(confirmed.favorite_organization_ids)
          applyFavoriteIds(robotIds, facilityIds, stationIds, organizationIds)
          const updatedAt = Date.now()
          pinsRevisionRef.current = updatedAt
          if (userIdRef.current) {
            publishFavoritePins({
              userId: userIdRef.current,
              robot: robotIds,
              facility: facilityIds,
              station: stationIds,
              organization: organizationIds,
              updatedAt,
            })
          }
        } catch (error) {
          applyFavoriteIds(previous.robot, previous.facility, previous.station, previous.organization)
          throw error
        } finally {
          pinsPendingRef.current -= 1
        }
      })
      favoriteChainRef.current = run.then(
        () => undefined,
        () => undefined
      )
      return run
    },
  }

  return (
    <UserProviderContext.Provider value={{ state, setState, actions }}>
      {children ? children : ''}
    </UserProviderContext.Provider>
  )
}

const KNOWN_AUDIENCES = new Set<UserAudience>([
  'developer',
  'serviceman',
  'internal_serviceman',
  'facility_manager',
  'reseller',
  'fleet_ops',
])

const normalizeAudience = (raw: WhoAmI['audience']): UserAudience | null => {
  // Rolling deploy: API may still return the retired `manager` value.
  if (raw === 'manager') {
    return 'reseller'
  }
  if ((raw as string | undefined) === 'autonomics_serviceman') {
    return 'internal_serviceman'
  }
  if (raw && KNOWN_AUDIENCES.has(raw as UserAudience)) {
    return raw as UserAudience
  }
  return null
}

const normalizeOrganization = (raw: unknown): OrganizationRef | null => {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const id = Number(record.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: String(record.name || ''),
    slug: String(record.slug || ''),
    is_active: record.is_active !== false,
    member_count: Number(record.member_count) || 0,
    can_assign_internal_serviceman: Boolean(record.can_assign_internal_serviceman),
  }
}

const normalizeAccount = (accountRaw: WhoAmI): IAccountState => {
  return {
    id: accountRaw.id,
    email: accountRaw.email,
    restorePasswordProgress: PROGRESS_TYPES.IDLE,
    loginProgress: PROGRESS_TYPES.SUCCESS,
    loginProgressError: LOGIN_ERROR_TYPES.NONE,
    userLoggedIn: true,
    firstName: accountRaw.first_name,
    lastName: accountRaw.last_name,
    audience: normalizeAudience(accountRaw.audience),
    organization: normalizeOrganization((accountRaw as WhoAmI & { organization?: unknown }).organization),
    canManageUsers: Boolean(accountRaw.can_manage_users),
    canUseIam: Boolean(accountRaw.can_use_iam),
    isSuperuser: accountRaw.is_superuser || false,
    isStaff: accountRaw.is_staff || false,
    isActive: accountRaw.is_active || false,
    groupIds: Array.isArray((accountRaw as any).group_ids)
      ? ((accountRaw as any).group_ids as number[]).filter((x): x is number => typeof x === 'number')
      : [],
    avatarUrl: typeof accountRaw.avatar_url === 'string' && accountRaw.avatar_url ? accountRaw.avatar_url : null,
    favoriteRobotIds: idList(accountRaw.favorite_robot_ids),
    favoriteFacilityIds: idList(accountRaw.favorite_facility_ids),
    favoriteStationIds: idList(accountRaw.favorite_station_ids),
    favoriteOrganizationIds: idList(accountRaw.favorite_organization_ids),
  }
}

export const getInitialAccountState = async (): Promise<IAccountState> => {
  try {
    const data = await AccountApi.fetchUser()
    return normalizeAccount(data)
  } catch (error) {
    return initialAccountState
  }
}
